import { useState, type FormEvent } from "react";
import { api, errorText } from "../api";
import { roleSummary, type AuthConfig, type PublicUser, type RoleNames } from "../types";
import { Banner, Field, PageHead, type Notice } from "../ui";

/** The signed-in user's account: who they are and changing their password. */
export function AccountPage({ user, roleNames, auth = { mode: "local" } }: { user: PublicUser; roleNames: RoleNames; auth?: AuthConfig }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [notice, setNotice] = useState<Notice>();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api("POST", "/auth/password", { currentPassword: current, newPassword: next });
      setCurrent("");
      setNext("");
      setNotice({ tone: "ok", text: "Password changed. Your other sessions have been signed out." });
    } catch (err) {
      setNotice({ tone: "bad", text: errorText(err) });
    }
  };

  const head = <PageHead title="Your account" sub={`${user.email} · ${roleSummary(user.memberships, roleNames)}`} />;
  if (auth.mode === "central") {
    return (
      <>
        {head}
        <section className="card stack" style={{ maxWidth: 460 }}>
          <h2>Central login</h2>
          <p>Your password, passkeys and two-step verification are managed in the central login, the same for every Resosys back-office.</p>
          <div><button className="primary" type="button" onClick={() => window.open(auth.accountUrl, "_blank", "noopener,noreferrer")}>Manage my login</button></div>
        </section>
      </>
    );
  }

  return (
    <>
      {head}
      <section className="card" style={{ maxWidth: 460 }}>
        <h2>Change password</h2>
        <Banner notice={notice} onClose={() => setNotice(undefined)} />
        <form className="stack" onSubmit={(e) => void submit(e)}>
          <Field label="Current password"><input type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} /></Field>
          <Field label="New password" help="At least 12 characters. A long phrase is easier to remember and more secure."><input type="password" autoComplete="new-password" minLength={12} required value={next} onChange={(e) => setNext(e.target.value)} /></Field>
          <div><button className="primary" type="submit">Save</button></div>
        </form>
      </section>
    </>
  );
}
